/**
 * Erreurs renvoyées par l'API Symfony, ramenées à une forme unique pour l'affichage.
 *
 * Le back répond selon trois formats :
 * - LexikJWT (connexion, jeton)  : { code, message }
 * - Symfony (requête invalide)   : { title, status, detail }
 * - API Platform (validation)    : { detail, violations: [{ propertyPath, message }] }
 */

/** Délai maximal d'un appel : au-delà, on considère le réseau (3G) comme perdu. */
const DELAI_MS = 20_000

export interface Violation {
  champ: string
  message: string
}

export class ApiError extends Error {
  readonly status: number
  readonly violations: Violation[]

  constructor(status: number, message: string, violations: Violation[] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.violations = violations
  }

  /** Premier message d'erreur de chaque champ, à afficher sous l'input correspondant. */
  get erreursChamps(): Record<string, string> {
    const erreurs: Record<string, string> = {}
    for (const { champ, message } of this.violations) erreurs[champ] ??= message
    return erreurs
  }
}

/** Jeton absent ou expiré pendant une action réservée à l'admin : il faut se reconnecter. */
export class SessionExpireeError extends ApiError {
  constructor() {
    super(401, 'Votre session a expiré. Reconnectez-vous.')
    this.name = 'SessionExpireeError'
  }
}

/** Le serveur n'a pas pu être joint (hors connexion, délai dépassé, CORS…). */
export class ReseauError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ReseauError'
  }
}

// Messages anglais connus du back → français. Une fois la traduction activée côté Symfony
// (framework.default_locale: fr), le back répond directement en français et ces règles ne
// s'appliquent plus : le message reçu est alors affiché tel quel.
const TRADUCTIONS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^Invalid credentials\.?$/i, () => 'E-mail ou mot de passe incorrect.'],
  [
    /^Too many failed login attempts, please try again in (\d+) minutes?\.?$/i,
    (m) => `Trop de tentatives échouées. Réessayez dans ${m[1]} minute${Number(m[1]) > 1 ? 's' : ''}.`,
  ],
  [/^JWT Token not found$/i, () => 'Vous devez être connecté pour faire cette action.'],
  [/^(Expired|Invalid) JWT Token$/i, () => 'Votre session a expiré. Reconnectez-vous.'],
  [/^(Invalid JSON\.?|The key ".+" must be provided\.?)$/i, () => 'Requête invalide envoyée au serveur.'],
  [/^Not Found$/i, () => 'Élément introuvable. Il a peut-être été supprimé.'],
  [/^No route found for /i, () => 'Le serveur ne propose pas encore cette fonction (route introuvable).'],
  [/^Access Denied\.?$/i, () => "Vous n'avez pas les droits pour faire cette action."],
  // Messages du validateur Symfony (contraintes des entités)
  [/^This value should not be (blank|null)\.$/i, () => 'Ce champ est obligatoire.'],
  [
    /^This value is too long\. It should have (\d+) characters? or less\.$/i,
    (m) => `Trop long : ${m[1]} caractères maximum.`,
  ],
  [/^This value should be of type .+\.$/i, () => 'Valeur invalide.'],
  [/^This value is not a valid URL\.$/i, () => 'Adresse web invalide.'],
  [/^This value is not a valid email address\.$/i, () => 'Adresse e-mail invalide.'],
  [/^This value is already used\.$/i, () => 'Cette valeur est déjà utilisée.'],
  [/^The value you selected is not a valid choice\.$/i, () => 'Choix non accepté par le serveur.'],
  [/^This value is not valid\.$/i, () => 'Valeur invalide.'],
  [/^This value should be either positive or zero\.$/i, () => 'La valeur doit être positive ou nulle.'],
  [/^This value should be between (-?[\d.]+) and (-?[\d.]+)\.$/i, (m) => `La valeur doit être comprise entre ${m[1]} et ${m[2]}.`],
]

export const traduire = (message: string): string => {
  for (const [regle, traduction] of TRADUCTIONS) {
    const m = message.trim().match(regle)
    if (m) return traduction(m)
  }
  return message
}

const messageParStatut = (status: number): string => {
  if (status >= 500) return `Le serveur a rencontré une erreur (${status}). Réessayez dans quelques minutes.`
  if (status === 403) return "Vous n'avez pas les droits pour faire cette action."
  if (status === 404) return 'Élément introuvable. Il a peut-être été supprimé.'
  if (status === 413) return 'Fichier trop volumineux.'
  return `Le serveur a refusé la demande (erreur ${status}).`
}

interface CorpsErreur {
  message?: string
  detail?: string
  description?: string
  'hydra:description'?: string
  violations?: { propertyPath: string; message: string }[]
}

/** Transforme une réponse HTTP en échec (status ≥ 400) en ApiError lisible. */
export async function lireErreur(res: Response): Promise<ApiError> {
  const corps = (await res.json().catch(() => null)) as CorpsErreur | null

  const violations = (corps?.violations ?? []).map((v) => ({ champ: v.propertyPath, message: traduire(v.message) }))
  if (violations.length) {
    return new ApiError(res.status, 'Certains champs sont invalides. Corrigez-les puis réessayez.', violations)
  }

  // Erreur serveur : le détail est technique (et peut contenir des informations internes) → message générique.
  const brut = res.status < 500 ? (corps?.message ?? corps?.detail ?? corps?.description ?? corps?.['hydra:description']) : undefined
  return new ApiError(res.status, brut ? traduire(brut) : messageParStatut(res.status))
}

/** fetch avec délai maximal et erreurs réseau en français. */
export async function appelerApi(url: string, init: RequestInit = {}): Promise<Response> {
  if (navigator.onLine === false) {
    throw new ReseauError('Vous êtes hors connexion. Vérifiez votre connexion internet.')
  }
  try {
    return await fetch(url, { ...init, signal: AbortSignal.timeout(DELAI_MS) })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ReseauError('Le serveur met trop de temps à répondre. Réessayez.')
    }
    throw new ReseauError('Serveur injoignable. Vérifiez votre connexion internet.')
  }
}
